import { route } from 'quasar/wrappers';
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory,
} from 'vue-router';

import routes from './routes';
import { useAuthStore } from 'src/stores/authStore';
import { api, setSessionRecovery } from 'src/boot/axios';

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default route(function (/* { store, ssrContext } */) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === 'history'
      ? createWebHistory
      : createWebHashHistory;

  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE),
  });
  const publicPages = [
    'login',
    'forgot-password',
    'register',
    'reset-password',
  ];

  const skipTrackingRoutes = new Set([
    'login',
    'register',
    'forgot-password',
    'reset-password',
    'logout',
  ]);

  const adminOnlyRoutes = new Set([
    'schools-admin',
    'articles-admin',
    'users',
    'activity',
  ]);

  setSessionRecovery(async () => {
    const authStore = useAuthStore();
    const previous = authStore.user;

    await authStore.updateUser();

    const current = authStore.user;
    if (!current) return false;

    if (current.was_admin_id && !previous?.was_admin_id) {
      await authStore.returnToAdmin();
      return true;
    }

    return previous?.id !== current.id;
  });

  Router.afterEach((to) => {
    const authStore = useAuthStore();
    if (!authStore.user) return;
    if (skipTrackingRoutes.has(to.name as string)) return;

    api
      .post('/activity/page-view', {
        path: to.fullPath,
        name: (to.name as string) ?? null,
        params: to.params,
      })
      .catch(() => {
        // Tracking is best-effort; never block navigation.
      });
  });

  Router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();
    const isPublicPage = publicPages.includes(to.name as string);

    // Controleer of de gebruiker niet is ingelogd en op een beveiligde pagina is
    if (!authStore.user && !isPublicPage) {
      await authStore.updateUser();
    }

    if (
      adminOnlyRoutes.has(to.name as string) &&
      authStore.user?.was_admin_id
    ) {
      try {
        await authStore.returnToAdmin();
      } catch {
        return next({ name: 'dashboard' });
      }
    }

    // Controleer of de route authenticatie vereist en of de gebruiker is ingelogd
    if (
      to.matched.some((record) => record.meta.requiresAuth) &&
      !authStore.user
    ) {
      next({ name: 'login' });
    } else {
      // Controleer op specifieke rolvereisten op basis van meta
      const requiredRole = to.meta.requiredRole as string;
      if (
        requiredRole &&
        authStore.user &&
        authStore.user.role !== requiredRole
      ) {
        next({ name: 'login' }); // Of een andere route die toegang geweigerd toont
      } else {
        next();
      }
    }
  });

  return Router;
});
